/**
 * PermisAmSensibilisation.jsx — Module 3 · Sensibilisation (2 h)
 * Séquence réglementaire de la Formation AM, en présence d'un parent/représentant légal (mineur).
 * 15 sections + bilan + message final.
 * Comporte une section interactive « Que ferais-tu ? » (scénarios) et une checklist d'engagement personnel.
 */
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  Home, Maximize2, CheckCircle2, Sparkles, ShieldAlert, ArrowLeft,
  AlertTriangle, HelpCircle, Lightbulb, Users, Award,
} from 'lucide-react';
import { cours } from '../../data/cours/permisAmSensibilisation';
import useSeo from '../../hooks/useSeo';
import { FullscreenOverlay, enterFullscreen } from '../../components/FullscreenMode';
import { QuizLauncher } from '../../components/QuizLauncher';
import { RefBadge } from '../../components/RefBadge';
import { SafeVideoEmbed } from '../../components/SafeVideoEmbed';

const SENSIBILISATION_VIDEOS = [
  {
    videoId: 'chTLzBlEtzE',
    title: 'Sensibilisation Permis AM — vidéo officielle',
    channel: 'Sécurité routière',
  },
  {
    videoId: 'm6jPvzGomnA',
    title: 'Sensibilisation Permis AM — témoignage / campagne',
    channel: 'Sécurité routière',
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
   Bloc « À retenir »
   ═══════════════════════════════════════════════════════════════════════════ */
function Retenir({ text }) {
  if (!text) return null;
  return (
    <div
      className="mt-6 rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-600/10 via-amber-500/5 to-transparent p-4 sm:p-5 flex items-start gap-3"
      data-testid="retenir-block"
    >
      <div className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
        <Sparkles className="w-4 h-4 text-amber-400" />
      </div>
      <div>
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-400 mb-1">À retenir</div>
        <p className="text-sm sm:text-base font-black leading-snug text-foreground" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          {text}
        </p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Carte scénario interactive « Que ferais-tu ? »
   ═══════════════════════════════════════════════════════════════════════════ */
function ScenarioCard({ scenario }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className={`rounded-2xl border-2 transition-all overflow-hidden ${
        expanded
          ? 'border-amber-500/60 bg-gradient-to-br from-amber-600/15 to-transparent'
          : 'border-white/10 bg-black/20 hover:border-amber-500/40'
      }`}
      data-testid={`scenario-${scenario.id}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left p-4 sm:p-5"
        aria-expanded={expanded}
        data-testid={`scenario-toggle-${scenario.id}`}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-1 w-10 h-10 rounded-full bg-amber-500/15 border-2 border-amber-500/40 flex items-center justify-center">
            <HelpCircle className="w-5 h-5 text-amber-400" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-400 mb-1">
              Que ferais-tu ?
            </div>
            <h4
              className="text-base sm:text-lg font-black leading-tight text-foreground"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {scenario.title}
            </h4>
            <p className="text-sm text-muted-foreground mt-1">{scenario.question}</p>
          </div>
          <div
            className={`flex-shrink-0 text-xs font-black uppercase tracking-widest text-amber-400 transition-transform ${
              expanded ? 'rotate-180' : ''
            }`}
            aria-hidden="true"
          >
            ▼
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 sm:px-5 pb-5 pt-1 space-y-4 animate-in fade-in duration-200">
          {/* Ce qui change */}
          <div className="rounded-xl border border-red-500/25 bg-red-500/5 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <div className="text-[10px] font-black uppercase tracking-widest text-red-400">
                Ce qui change · Risques
              </div>
            </div>
            <ul className="space-y-1.5">
              {scenario.changes.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground/90">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Solution */}
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Lightbulb className="w-4 h-4 text-emerald-400" />
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-400">
                Ma solution
              </div>
            </div>
            <p className="text-sm text-foreground/90 leading-relaxed italic" style={{ fontFamily: "'Crimson Text', Georgia, serif" }}>
              {scenario.solution}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Checklist d'engagement personnel (section 15)
   ═══════════════════════════════════════════════════════════════════════════ */
function EngagementList({ items }) {
  const [checked, setChecked] = useState(() => items.map(() => false));
  const total = items.length;
  const done = useMemo(() => checked.filter(Boolean).length, [checked]);
  const toggle = (i) => setChecked((prev) => prev.map((v, idx) => (idx === i ? !v : v)));

  return (
    <div className="space-y-3" data-testid="engagement-list">
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <Award className="w-4 h-4 text-amber-400" />
          <div className="text-[10px] font-black uppercase tracking-widest text-amber-400">
            Ma progression
          </div>
        </div>
        <div className="text-sm font-black text-foreground tabular-nums">
          {done} / {total}
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-amber-500 to-red-500 transition-all duration-300"
          style={{ width: `${(done / total) * 100}%` }}
        />
      </div>
      <div className="space-y-2 mt-2">
        {items.map((label, i) => (
          <button
            key={i}
            type="button"
            onClick={() => toggle(i)}
            className={`w-full text-left flex items-start gap-3 p-3.5 rounded-xl border-2 transition-all ${
              checked[i]
                ? 'border-emerald-500/50 bg-emerald-500/10'
                : 'border-white/10 bg-black/20 hover:border-amber-500/40 hover:bg-amber-500/5'
            }`}
            data-testid={`engagement-item-${i}`}
            aria-pressed={checked[i]}
          >
            <div
              className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                checked[i] ? 'border-emerald-500 bg-emerald-500' : 'border-white/30 bg-transparent'
              }`}
              aria-hidden="true"
            >
              {checked[i] && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
            </div>
            <span
              className={`text-sm leading-relaxed transition-colors ${
                checked[i] ? 'text-emerald-200' : 'text-foreground/90'
              }`}
              style={{ fontFamily: "'Crimson Text', Georgia, serif" }}
            >
              {label}
            </span>
          </button>
        ))}
      </div>
      {done === total && (
        <div className="mt-4 rounded-xl border-2 border-emerald-500/50 bg-emerald-500/10 p-4 text-center animate-in fade-in duration-500">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-400 mb-1">
            Bravo
          </div>
          <p className="text-sm font-black text-emerald-200" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Tu viens de poser tes propres règles. C&apos;est ça, devenir conducteur.
          </p>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Bloc de section (rendu polymorphe : list / cards3 / scenarios / engagement)
   ═══════════════════════════════════════════════════════════════════════════ */
function Section({ section }) {
  const numSize = 'text-5xl md:text-6xl';
  const titleSize = 'text-xl md:text-2xl';

  return (
    <section className="c2r-cours-section" data-testid={`section-${section.num}`}>
      <div className="flex items-start gap-5 mb-5">
        <span
          className={`${numSize} font-black leading-none text-amber-500/25`}
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {section.num}
        </span>
        <div className="flex-1 pt-1">
          <p className="text-[10px] md:text-[11px] font-black uppercase tracking-[0.22em] text-amber-500 mb-2">
            {section.kicker}
            <RefBadge slug={cours.slug} num={section.num} />
          </p>
          <h2
            className={`${titleSize} font-extrabold leading-tight tracking-tight text-foreground`}
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {section.title}
          </h2>
        </div>
      </div>

      {section.lead && (
        <p className="text-[15px] md:text-base leading-relaxed text-muted-foreground mb-4">
          {section.lead}
        </p>
      )}

      {/* Type LIST */}
      {section.type === 'list' && section.list && (
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          {section.list.map((item, i) => (
            <div key={i} className="p-4 rounded-xl border border-white/10 bg-black/20">
              <div className="text-[11px] font-black uppercase tracking-widest text-amber-400 mb-1">
                {item.label}
              </div>
              <p className="text-sm text-foreground/90 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      )}

      {/* Type CARDS3 */}
      {section.type === 'cards3' && section.cards && (
        <div className="grid md:grid-cols-3 gap-3 mb-4">
          {section.cards.map((c, i) => (
            <div
              key={i}
              className="p-4 rounded-xl border border-white/10 bg-gradient-to-br from-amber-600/10 to-transparent"
            >
              <div className="text-sm font-black uppercase tracking-wide text-amber-400 mb-2">
                {c.title}
              </div>
              <p className="text-sm text-foreground/90 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      )}

      {/* Type SCENARIOS — cartes interactives « Que ferais-tu ? » */}
      {section.type === 'scenarios' && section.scenarios && (
        <div className="space-y-3 mb-4" data-testid="scenarios-grid">
          {section.scenarios.map((s) => (
            <ScenarioCard key={s.id} scenario={s} />
          ))}
        </div>
      )}

      {/* Type ENGAGEMENT — checklist personnelle */}
      {section.type === 'engagement' && section.engagements && (
        <EngagementList items={section.engagements} />
      )}

      <Retenir text={section.retenir} />
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Page principale
   ═══════════════════════════════════════════════════════════════════════════ */
export default function PermisAmSensibilisation() {
  const [present, setPresent] = useState(false);
  useSeo({
    title: `${cours.title} — Formation Permis AM · Code 2 Roues`,
    description: cours.intro.slice(0, 160),
  });

  return (
    <div className="min-h-screen bg-background text-foreground pb-20 print:pb-0">
      {/* Barre du haut */}
      <div className="sticky top-0 z-40 border-b border-white/10 bg-background/95 backdrop-blur print:hidden">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <Button asChild variant="outline" size="sm" data-testid="btn-back-am">
            <Link to="/permis-am" reloadDocument>
              <ArrowLeft className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline"> Permis AM</span>
            </Link>
          </Button>
          <div className="hidden md:block flex-1 text-center">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-500">
              Module 3 · Sensibilisation
            </div>
          </div>
          <div className="flex-1 md:hidden" />
          <Button asChild variant="ghost" size="sm">
            <Link to="/" reloadDocument>
              <Home className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline"> Accueil</span>
            </Link>
          </Button>
          <Button
            onClick={() => enterFullscreen(() => setPresent(true))}
            size="sm"
            className="bg-amber-600 hover:bg-amber-500 text-white"
            data-testid="btn-present"
          >
            <Maximize2 className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline"> Plein écran</span>
          </Button>
        </div>
      </div>

      <article className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* HERO */}
        <header className="mb-10 text-center" data-testid="cours-hero">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-[10px] font-black uppercase tracking-widest text-amber-400 mb-4">
            <ShieldAlert className="w-3 h-3" /> Module 3 · Sensibilisation · 2 h
          </div>
          <h1
            className="text-3xl sm:text-5xl md:text-6xl font-black leading-tight mb-3"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {cours.title.replace('Module 3 — ', '')}
          </h1>
          <p className="text-lg md:text-xl text-amber-500 font-black tracking-wide mb-6">
            {cours.subtitle}
          </p>

          {/* Bandeau parents obligatoire */}
          <div className="max-w-2xl mx-auto p-3 rounded-xl border border-red-500/40 bg-red-500/10 flex items-center gap-3 mb-5">
            <Users className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-xs sm:text-sm text-foreground/95 text-left">
              <strong className="text-red-300">Présence obligatoire</strong> d&apos;un parent ou représentant légal si l&apos;élève est mineur.
            </p>
          </div>

          {/* Mantra */}
          <div className="max-w-2xl mx-auto p-4 rounded-xl border border-amber-500/30 bg-amber-500/5">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-400 mb-2">
              Fil conducteur
            </div>
            <div
              className="text-sm md:text-base font-black text-foreground"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {cours.mantra}
            </div>
          </div>

          <p className="mt-6 text-sm text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {cours.intro}
          </p>
        </header>

        {/* SOMMAIRE */}
        <nav className="mb-10 p-4 rounded-xl border border-white/10 bg-black/20" data-testid="sommaire">
          <div className="text-[11px] font-black uppercase tracking-widest text-amber-400 mb-3">
            Sommaire · {cours.sections.length} séquences
          </div>
          <div className="grid sm:grid-cols-2 gap-1.5">
            {cours.sections.map((s) => (
              <a
                key={s.num}
                href={`#section-${s.num}`}
                className="text-sm text-foreground/80 hover:text-amber-400 transition py-0.5"
              >
                <span className="font-black text-amber-500/60 mr-2">{s.num}</span>
                {s.title}
              </a>
            ))}
          </div>
        </nav>

        {/* SECTIONS */}
        <div className="space-y-10">
          {cours.sections.map((s) => (
            <div key={s.num} id={`section-${s.num}`}>
              <Section section={s} />
            </div>
          ))}
        </div>

        {/* VIDÉOS SENSIBILISATION — RGPD-safe */}
        <section
          id="videos-sensibilisation"
          className="mt-14 p-6 md:p-8 rounded-2xl border-2 border-red-500/30 bg-gradient-to-br from-red-600/10 via-amber-500/5 to-transparent"
          data-testid="videos-sensibilisation"
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-400">Vidéos officielles</div>
              <h3 className="text-lg md:text-xl font-black text-foreground leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                À visionner avec ton parent — pour ancrer les messages clés
              </h3>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-5 max-w-3xl">
            Ces vidéos officielles complètent la séquence de sensibilisation. Elles sont intégrées en mode <strong>youtube-nocookie</strong> :
            aucune donnée n'est transmise à YouTube tant que tu n'as pas cliqué sur « lire ».
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {SENSIBILISATION_VIDEOS.map((v) => (
              <SafeVideoEmbed
                key={v.videoId}
                videoId={v.videoId}
                title={v.title}
                channel={v.channel}
                testid={`video-sensibilisation-${v.videoId}`}
              />
            ))}
          </div>
        </section>

        {/* BILAN FINAL */}
        {cours.bilan && (
          <section
            className="mt-14 p-6 md:p-8 rounded-2xl border-2 border-amber-500/40 bg-gradient-to-br from-amber-600/15 via-amber-500/8 to-transparent"
            data-testid="bilan-final"
          >
            <div className="flex items-center justify-center gap-2 mb-3">
              <CheckCircle2 className="w-5 h-5 text-amber-400" />
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-400">
                Bilan · ce que tu dois retenir
              </div>
            </div>
            <h3
              className="text-xl md:text-2xl font-black text-foreground text-center mb-2"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {cours.bilan.title}
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-5">{cours.bilan.intro}</p>
            <ul className="space-y-2 max-w-2xl mx-auto">
              {cours.bilan.items.map((it, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-sm text-foreground/90 p-3 rounded-lg border border-white/10 bg-black/20"
                >
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* MESSAGE FINAL */}
        {cours.message && (
          <section
            className="mt-8 p-6 md:p-10 rounded-2xl border-2 border-red-500/40 bg-gradient-to-br from-red-600/15 via-amber-500/5 to-transparent text-center"
            data-testid="message-final"
          >
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/15 border border-red-500/30 text-[10px] font-black uppercase tracking-widest text-red-400 mb-4">
              <Sparkles className="w-3 h-3" /> Le message final
            </div>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-6 italic" style={{ fontFamily: "'Crimson Text', Georgia, serif" }}>
              {cours.message.body}
            </p>
            <p
              className="text-lg md:text-2xl font-black text-foreground leading-tight max-w-3xl mx-auto"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {cours.message.punchline}
            </p>
          </section>
        )}

        {/* QUIZ D'ANCRAGE */}
        <section className="mt-10" data-testid="quiz-section">
          <QuizLauncher quizId="permis-am-sensibilisation" color="#f59e0b" />
        </section>

        {/* RETOUR MODULE */}
        <div
          className="mt-12 p-6 rounded-2xl border-2 border-amber-500/30 bg-gradient-to-br from-amber-600/15 to-transparent text-center"
          data-testid="footer-nav"
        >
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-400 mb-2">
            Retour au module
          </div>
          <h3
            className="text-xl md:text-2xl font-black text-foreground mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Formation AM · 8 heures · 3 modules
          </h3>
          <div className="flex flex-wrap justify-center gap-2">
            <Button asChild className="bg-amber-600 hover:bg-amber-500 text-white" data-testid="btn-bottom-back-am">
              <Link to="/permis-am" reloadDocument>Retour au hub AM</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/cours/permis-am-journee-2" reloadDocument>Revoir Journée 2</Link>
            </Button>
          </div>
        </div>
      </article>

      {present && <FullscreenOverlay onExit={() => setPresent(false)} />}
    </div>
  );
}
